/**
 * Animated scroll to an element inside its nearest scrollable ancestor.
 *
 * The native `Element.scrollIntoView({ behavior: "smooth" })` gives no control
 * over duration/easing and, inside nested `overflow` containers (as used by the
 * item modal), often resolves almost instantly — which reads as an abrupt jump
 * rather than a scroll. This drives `scrollTop` ourselves with rAF + easing so
 * the transition is always a visible, consistent glide.
 *
 * Note: this intentionally animates regardless of `prefers-reduced-motion` — the
 * group-to-group glide is an explicit, core part of the ordering flow, and a
 * short content-following scroll is benign. Pass `duration: 0` to jump instead.
 */

/** Walks up the DOM for the first ancestor that actually scrolls vertically. */
function getScrollableParent(element: HTMLElement): HTMLElement {
  let node: HTMLElement | null = element.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    const scrolls = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
    if (scrolls && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return document.scrollingElement as HTMLElement;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

interface SmoothScrollOptions {
  /** Gap (px) left above the element, mirroring CSS `scroll-mt`. Default 80. */
  offset?: number;
  /** Animation length in ms. Default 550. */
  duration?: number;
  /**
   * Scroll container to drive. When omitted, the nearest scrollable ancestor is
   * detected. Pass it explicitly when the DOM has nested `overflow` containers
   * (as the item modal does) to remove any ambiguity about which one moves.
   */
  container?: HTMLElement | null;
}

/** Tracks the active animation per container so a new call cancels the old one. */
const activeAnimations = new WeakMap<HTMLElement, number>();

export function smoothScrollToElement(
  element: HTMLElement,
  { offset = 80, duration = 550, container: explicitContainer }: SmoothScrollOptions = {}
): void {
  const container = explicitContainer ?? getScrollableParent(element);

  const containerTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
  const targetTop =
    container.scrollTop + (element.getBoundingClientRect().top - containerTop) - offset;

  const maxScroll = container.scrollHeight - container.clientHeight;
  const destination = Math.max(0, Math.min(targetTop, maxScroll));
  const start = container.scrollTop;
  const distance = destination - start;

  const pending = activeAnimations.get(container);
  if (pending !== undefined) cancelAnimationFrame(pending);

  if (Math.abs(distance) < 1) return;

  if (duration <= 0) {
    container.scrollTop = destination;
    return;
  }

  const startTime = performance.now();

  const step = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    container.scrollTop = start + distance * easeInOutCubic(progress);

    if (progress < 1) {
      activeAnimations.set(container, requestAnimationFrame(step));
    } else {
      activeAnimations.delete(container);
    }
  };

  activeAnimations.set(container, requestAnimationFrame(step));
}
